import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Play, MessageCircle, Check } from "lucide-react";
import YmfLogo, { WinkFace } from "./YmfLogo";
import { goTo } from "../lib/scroll";

const lineAnim = (i) => ({
  initial: { y: "112%" },
  animate: { y: "0%" },
  transition: { duration: 0.85, delay: 0.15 + i * 0.13, ease: [0.22, 1, 0.36, 1] },
});

const CHIPS = [
  "Coordinado por Patricia Bilbao, experta en Lectura Fácil (UNED)",
  "Cada texto se valida con personas del CAI de Pando, Langreo",
  "Porque entender es un derecho, no un favor",
];

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yArt = useTransform(scrollYProgress, [0, 1], [0, 64]);
  const yPhoto = useTransform(scrollYProgress, [0, 1], [0, -36]);

  return (
    <section id="top" ref={ref} className="relative pt-28 md:pt-36 pb-16 md:pb-24 overflow-hidden">
      <div className="max-w-6xl mx-auto px-5 md:px-8 grid lg:grid-cols-[1.1fr_0.9fr] gap-12 lg:gap-8 items-center">
        {/* Columna de texto */}
        <div>
          <motion.p
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2.5 sketch-sm bg-[var(--card)] px-4 py-2 text-[11px] sm:text-xs font-black uppercase tracking-[0.16em]"
            data-testid="hero-badge"
          >
            <span className="w-2 h-2 rounded-full bg-[var(--blue)] animate-pulse" aria-hidden="true" />
            Asturias · Lectura fácil · Norma UNE 153101
          </motion.p>

          <h1 className="font-display font-extrabold tracking-tight leading-[1.04] text-[clamp(2.7rem,7.2vw,5.3rem)] mt-7">
            <span className="block overflow-hidden pb-1">
              <motion.span className="block" {...lineAnim(0)}>
                Lo difícil,
              </motion.span>
            </span>
            <span className="block overflow-hidden pb-2">
              <motion.span className="block" {...lineAnim(1)}>
                <span className="marker">muy fácil.</span>
              </motion.span>
            </span>
          </h1>

          <motion.span
            initial={{ scale: 0, rotate: -30 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ delay: 0.85, type: "spring", stiffness: 260, damping: 14 }}
            className="inline-block -mt-1 ml-1"
          >
            <WinkFace size={52} className="drop-shadow-[3px_3px_0_var(--ink)]" />
          </motion.span>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.7 }}
            className="mt-6 text-lg md:text-xl leading-relaxed max-w-xl text-[var(--muted)]"
          >
            Traducimos textos difíciles a lectura fácil. Leyes, trámites, cartas médicas y noticias. Escritos para que todas las personas los entiendan. Sin resumir. Sin infantilizar. Con método.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.75, duration: 0.7 }}
            className="mt-8 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-cta-podcast"
              onClick={() => goTo("#podcast")}
              className="inline-flex items-center gap-2.5 sketch-sm bg-[var(--yellow)] px-6 py-3.5 text-base font-bold shadow-[4px_4px_0_var(--ink)] hover:-translate-y-0.5 active:translate-y-0 transition-transform"
            >
              <Play className="w-5 h-5 fill-current" aria-hidden="true" />
              Ver el vídeo podcast
            </button>
            <a
              href="https://wa.me/34623409416"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-cta-whatsapp"
              className="inline-flex items-center gap-2.5 sketch-sm bg-[var(--card)] px-6 py-3.5 text-base font-bold hover:-translate-y-0.5 transition-transform"
            >
              <MessageCircle className="w-5 h-5" aria-hidden="true" />
              WhatsApp 623 409 416
            </a>
          </motion.div>

          <motion.ul
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.95, duration: 0.7 }}
            className="mt-10 space-y-2.5"
            data-testid="hero-trust-chips"
          >
            {CHIPS.map((c) => (
              <li key={c} className="flex items-start gap-2.5 text-[15px] font-bold">
                <span className="mt-0.5 inline-flex items-center justify-center w-5 h-5 shrink-0 sketch-sm bg-[var(--blue)] text-white">
                  <Check className="w-3 h-3" strokeWidth={3.5} aria-hidden="true" />
                </span>
                {c}
              </li>
            ))}
          </motion.ul>
        </div>

        {/* Columna artística con parallax */}
        <motion.div style={{ y: yArt }} className="relative hidden md:block" data-testid="hero-art">
          <div className="sketch-2 bg-[var(--card)] rotate-2 p-10 md:p-12 shadow-[10px_10px_0_var(--ink)] max-w-md mx-auto">
            <YmfLogo size={280} className="mx-auto w-full h-auto max-w-[280px]" withWink />
            <p className="font-hand text-3xl text-center mt-2">Ye muy fácil</p>
          </div>
          <motion.p
            animate={{ y: [0, -8, 0], rotate: [-7, -5, -7] }}
            transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
            className="absolute -left-4 top-8 sketch-sm bg-[var(--yellow)] px-4 py-2 font-hand text-xl shadow-[3px_3px_0_var(--ink)]"
          >
            ¡Se entiende a la primera!
          </motion.p>
          <motion.p
            animate={{ y: [0, 8, 0], rotate: [4, 2, 4] }}
            transition={{ duration: 6, repeat: Infinity, ease: "easeInOut", delay: 0.8 }}
            className="absolute -right-2 -bottom-6 sketch-sm bg-[var(--blue)] text-white px-4 py-2 font-hand text-xl shadow-[3px_3px_0_var(--ink)]"
          >
            Validado por personas
          </motion.p>
          <motion.div
            style={{ y: yPhoto }}
            className="absolute -bottom-14 -left-8 w-44 sketch bg-[var(--card)] p-1.5 -rotate-6 shadow-[5px_5px_0_var(--ink)]"
          >
            <img
              src="https://images.unsplash.com/photo-1783788488923-76663cdf616f?crop=entropy&cs=srgb&fm=jpg&q=85"
              alt="Dos personas leyendo un libro juntas y sonriendo"
              className="w-full h-32 object-cover sketch-sm"
              loading="eager"
            />
            <p className="font-hand text-base text-center py-1">Leer en compañía</p>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
